import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  DollarSign,
  Hash,
  Loader,
} from 'lucide-react';
import { toast } from 'sonner';
// Firebase imports - Supabase removed

interface PublicInvoiceUploadProps {
  ticketId: string;
}

export default function PublicInvoiceUpload({ ticketId }: PublicInvoiceUploadProps) {
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  
  // Form state
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [uploadedBy, setUploadedBy] = useState('');
  const [markComplete, setMarkComplete] = useState(true);

  useEffect(() => {
    loadTicket();
  }, [ticketId]);

  const loadTicket = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-bc7204ee/service-tickets/${ticketId}`,
        {
          headers: { Authorization: `Bearer ${publicAnonKey}` },
        }
      );

      if (response.ok) {
        const ticketData = await response.json();
        setTicket(ticketData);
        
        // Check if invoice already uploaded
        const invoiceResponse = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-bc7204ee/service-tickets/${ticketId}/invoice`,
          {
            headers: { Authorization: `Bearer ${publicAnonKey}` },
          }
        );
        
        if (invoiceResponse.ok) {
          const invoiceData = await invoiceResponse.json();
          if (invoiceData) {
            setUploaded(true);
          }
        }
      } else {
        toast.error('Service ticket not found');
      }
    } catch (error) {
      console.error('Error loading ticket:', error);
      toast.error('Failed to load service ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a PDF or image file (JPG, PNG)');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      setInvoiceFile(file);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!invoiceFile) {
      toast.error('Please select an invoice file to upload');
      return;
    }

    if (!invoiceNumber.trim()) {
      toast.error('Invoice number is required');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid invoice amount');
      return;
    }

    if (!uploadedBy.trim()) {
      toast.error('Please enter your name/company');
      return;
    }

    setUploading(true);

    try {
      // Convert file to base64
      const fileData = await fileToBase64(invoiceFile);

      const invoiceData = {
        fileName: invoiceFile.name,
        fileSize: invoiceFile.size,
        fileType: invoiceFile.type,
        fileData,
        invoiceNumber,
        amount: parseFloat(amount),
        notes,
        uploadedBy,
        markComplete,
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-bc7204ee/service-tickets/${ticketId}/invoice`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${publicAnonKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(invoiceData),
        }
      );

      if (response.ok) {
        const result = await response.json();
        
        // Send email notification to admin
        try {
          await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-bc7204ee/invoices/send-upload-notification`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${publicAnonKey}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                invoice: result.invoice,
              }),
            }
          );
        } catch (emailError) {
          console.error('Failed to send email notification:', emailError);
          // Don't fail the whole upload if email fails
        }
        
        setUploaded(true);
        toast.success('Invoice uploaded successfully!', {
          description: 'The company has been notified',
        });
      } else {
        throw new Error('Failed to upload invoice');
      }
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Failed to upload invoice. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        resolve(base64.split(',')[1]); // Remove data:*/*;base64, prefix
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 w-full max-w-md">
          <div className="text-center">
            <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-700">Loading service ticket...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="p-8 w-full max-w-md text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Not Found</h2>
          <p className="text-gray-600">
            The service ticket you're looking for doesn't exist or has been removed.
          </p>
        </Card>
      </div>
    );
  }

  if (uploaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="p-8 w-full max-w-md text-center shadow-2xl">
          <div className="mb-6">
            <div className="h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Invoice Uploaded!</h2>
            <p className="text-gray-600 text-lg">
              Thank you for submitting your invoice
            </p>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Ticket #{ticket.ticketNumber}</strong>
            </p>
            <p className="text-sm text-gray-600">
              The company has been notified and will process your invoice shortly.
            </p>
          </div>

          <div className="space-y-2 text-sm text-gray-600">
            <p>✓ Invoice saved successfully</p>
            <p>✓ Notification sent to company</p>
            <p>✓ Ticket marked as {markComplete ? 'completed' : 'in progress'}</p>
          </div>

          <p className="text-xs text-gray-500 mt-6">
            You can close this window now
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Upload Invoice</h1>
          <p className="text-gray-600">Submit your invoice for service ticket</p>
        </div>

        {/* Ticket Info Card */}
        <Card className="mb-6 border-2 border-blue-200">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Service Ticket Details</h2>
              <Badge className="bg-blue-600 text-white">
                {ticket.ticketNumber}
              </Badge>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Service Type</p>
                  <p className="font-medium text-gray-900 capitalize">
                    {ticket.serviceType?.replace(/_/g, ' ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-1">Status</p>
                  <Badge
                    className={
                      ticket.status === 'completed'
                        ? 'bg-green-100 text-green-800'
                        : ticket.status === 'in-progress'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }
                  >
                    {ticket.status}
                  </Badge>
                </div>
              </div>

              {ticket.title && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Description</p>
                  <p className="text-gray-900">{ticket.title}</p>
                </div>
              )}

              {ticket.wellName && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Well</p>
                  <p className="font-medium text-gray-900">{ticket.wellName}</p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Upload Form */}
        <Card className="shadow-xl">
          <div className="p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Invoice Information</h3>

            <div className="space-y-5">
              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice File <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                    id="invoice-upload"
                  />
                  <label htmlFor="invoice-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    {invoiceFile ? (
                      <div>
                        <p className="font-medium text-gray-900 mb-1">{invoiceFile.name}</p>
                        <p className="text-sm text-gray-500">
                          {(invoiceFile.size / 1024).toFixed(2)} KB
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-3"
                          onClick={(e) => {
                            e.preventDefault();
                            setInvoiceFile(null);
                          }}
                        >
                          Change File
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-600 mb-1">Click to upload or drag and drop</p>
                        <p className="text-xs text-gray-500">PDF, JPG, PNG (max 5MB)</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Invoice Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    placeholder="e.g., INV-2024-001"
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Amount <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    type="number"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              {/* Uploaded By */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name / Company <span className="text-red-500">*</span>
                </label>
                <Input
                  value={uploadedBy}
                  onChange={(e) => setUploadedBy(e.target.value)}
                  placeholder="e.g., ABC Services Inc."
                  className="h-12"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional notes or comments..."
                  rows={4}
                />
              </div>

              {/* Mark Complete Checkbox */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={markComplete}
                    onChange={(e) => setMarkComplete(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900">Mark service as complete</span>
                    <p className="text-sm text-gray-600 mt-1">
                      Check this if the service work has been fully completed
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-8">
              <Button
                onClick={handleSubmit}
                disabled={uploading}
                className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                {uploading ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    Uploading Invoice...
                  </>
                ) : (
                  <>
                    <Upload className="h-5 w-5 mr-2" />
                    Submit Invoice
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-4">
              By submitting this invoice, you confirm that all information is accurate
            </p>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-600">
          <p>Having trouble? Contact support for assistance</p>
        </div>
      </div>
    </div>
  );
}